import moment from "moment-timezone";

// Leg and contract `departureDate` / `returnDate` are date-only markers pinned to
// UTC midnight; the real time of day lives on `etd` / `eta` alongside the leg's
// timezone. Read the date part in UTC, never in the viewer's zone, or the marker
// rolls back a day for anyone west of UTC. This matches the server's canonical
// readers (TripService.formatDateAndTime, beforeSaveItinerary, verificationDeadline).
const formatTripDate = (value, format = "M/D/YY") =>
  value ? moment.utc(value).format(format) : "";

// "first - last" label for the accounting tables and trip pickers.
// `firstDepartureDate` is the first leg's date-only marker (read in UTC).
// `lastArrivalDate` is the last leg's eta/etd, a true instant that the
// server projects without its airport zone, so it is read in the viewer's
// zone until the projection carries the zone (ENGINEERING_FOLLOWUPS F12).
const formatTripDateRange = (firstDepartureDate, lastArrivalDate) =>
  [
    formatTripDate(firstDepartureDate),
    lastArrivalDate ? moment(lastArrivalDate).format("M/D/YY") : "",
  ]
    .filter(Boolean)
    .join(" - ");

// `etd` / `eta` are true instants: render them in the airport's own timezone.
const formatLegTime = (value, timeZone, format = "h:mma z") =>
  value ? moment.tz(value, timeZone || "America/New_York").format(format) : "";

// Sortable calendar day for a date-only marker, so legs on the same day don't
// interleave when some are stored at 00:00Z and others at 04:00Z.
const tripDateSortKey = (value) =>
  value ? moment.utc(value).format("YYYY-MM-DD") : "";

// Any date-ish value -> a date-only marker at true UTC midnight, keeping the
// calendar day it already carries and dropping the time of day.
const toDepartureDateMarker = (value) =>
  value ? moment.utc(value).startOf("day").toISOString() : value;

// Prefer a leg's ETD (a true instant, read in the airport's zone); fall back to
// the date-only marker, read in UTC.
const formatLegDateLabel = (leg, format = "MM/DD/YYYY") =>
  leg?.etd
    ? formatLegTime(leg.etd, leg.departureTimeZone, format)
    : formatTripDate(leg?.departureDate, format);

// Date-only marker -> local-midnight Date, for widgets that display in local time.
function convertUTCToLocalDate(date) {
  if (!date) {
    return date;
  }
  date = new Date(date);
  date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return date;
}

// Local-midnight Date -> date-only marker at true UTC midnight, whatever the
// viewer's timezone.
function convertLocalToUTCDate(date) {
  if (!date) {
    return date;
  }
  date = new Date(date);
  date = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  return date;
}

export {
  formatTripDate,
  formatTripDateRange,
  formatLegTime,
  formatLegDateLabel,
  toDepartureDateMarker,
  tripDateSortKey,
  convertUTCToLocalDate,
  convertLocalToUTCDate,
};
